// Escalation attempts for a session, read from the conversation_escalated /
// escalation_missed events (GET /sessions/{id} → escalations). Dashboard-only:
// the visitor's widget never receives the assigned rep.

export interface SessionEscalationRep {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  profile_photo_url?: string | null;
}

export interface SessionEscalation {
  id: string;
  status: 'escalated' | 'missed';
  created_at?: string | null;
  reason?: string | null;
  escalated_by?: string | null;
  assigned_to?: SessionEscalationRep | null;
  assigned_via?: 'account_owner' | 'targeted' | 'rotation' | string | null;
  skill_id?: string | null;
  skill_name?: string | null;
  tool_call_id?: string | null;
}

// Events recorded before tool_call_id existed pair with their tool card by
// time: the event is written within moments of the call.
const LEGACY_MATCH_WINDOW_MS = 60_000;

export const escalationRepLabel = (rep?: SessionEscalationRep | null): string | null =>
  rep?.name || rep?.email || null;

export const assignedViaLabel = (assignedVia?: string | null): string | null => {
  switch (assignedVia) {
    case 'account_owner':
      return 'Account owner';
    case 'rotation':
      return 'Round robin';
    case 'targeted':
      return 'Chosen by AI';
    default:
      return null;
  }
};

export const escalatedByLabel = (escalatedBy?: string | null): string | null => {
  switch (escalatedBy) {
    case 'tool_call':
      return 'AI';
    case 'button':
      return 'Visitor';
    default:
      return escalatedBy || null;
  }
};

export const findEscalationForToolCall = (
  escalations: SessionEscalation[] | undefined | null,
  callId?: string | null,
  startedAt?: string | null,
): SessionEscalation | null => {
  if (!escalations?.length) return null;
  if (callId) {
    const exact = escalations.find((e) => e.tool_call_id === callId);
    if (exact) return exact;
  }
  const started = startedAt ? Date.parse(startedAt) : NaN;
  if (Number.isNaN(started)) return null;

  let best: SessionEscalation | null = null;
  let bestDelta = Infinity;
  for (const escalation of escalations) {
    if (escalation.tool_call_id || escalation.escalated_by !== 'tool_call' || !escalation.created_at) continue;
    const delta = Math.abs(Date.parse(escalation.created_at) - started);
    if (delta <= LEGACY_MATCH_WINDOW_MS && delta < bestDelta) {
      best = escalation;
      bestDelta = delta;
    }
  }
  return best;
};

export const latestEscalation = (
  escalations: SessionEscalation[] | undefined | null,
): SessionEscalation | null => (escalations?.length ? escalations[escalations.length - 1] : null);
